<template>
  <recess-link
    :to="documentPath(product)"
    :class="classes"
    :style="{
      '--grid-item-background-color-resting': backgroundColorResting,
      '--grid-item-background-color-hover': backgroundColorHover,
      '--grid-item-text-color-hover': textColorHover
    }"
    class="product-grid-item link"
    @mouseover.native="over"
    @mouseleave.native="out"
    @touchstart.native="over"
    @touchend.native="out"
    @focus.native="over"
    @blur.native="out"
  >
    <div class="product-grid-item__image">
      <float
        v-if="product.thumb"
        tag="sanity-media"
        :reverse="index % 2 ? true : false"
        :offset="index % 2 ? '-2%' : '2%'"
        :speed="index % 2 ? 4 : 3"
        :stopped="bp && bp.sm && !active"
        :image="product.thumb.image"
        :max-size="true"
        not-lazy
        no-ratio
        fluid
        contain
      />
    </div>
    <div class="product-grid-item__title">
      <h4>{{ product.title }}</h4>
    </div>
    <badge v-if="product.badge" class="product-grid-item__badge text-xs">
      {{ product.badge }}
    </badge>
    <fade>
      <img
        v-if="soldOut"
        class="product-grid-item__sold-out"
        src="/sold-out.svg"
        alt="sold out"
      />
    </fade>
    <div>
      <slot />
    </div>
  </recess-link>
</template>

<script>
import { mapGetters } from 'vuex'
import { last } from 'lodash'
import query from '~/shopify/queries/product-available.gql'
import { colors } from '~/recess.json'

export default {
  props: {
    backgroundColor: {
      default: colors.apricot,
      required: false,
      type: String
    },
    product: {
      default: undefined,
      type: Object
    },
    index: {
      default: 0,
      type: Number
    },
    totalCols: {
      default: 0,
      type: Number
    },
    active: {
      default: false,
      type: Boolean
    }
  },

  data: () => ({
    isOver: false
  }),

  apollo: {
    available: {
      client: 'storefront',
      query,
      variables() {
        return {
          id: this.shopifyId
        }
      },
      update({ node }) {
        if (node && typeof node.availableForSale === 'boolean') {
          return node.availableForSale
        }
        return undefined
      },
      skip() {
        return !this.shopifyId
      },
      prefetch: false
    }
  },

  computed: {
    ...mapGetters('screen', {
      bp: 'breakpoint'
    }),
    classes() {
      return {
        'is-sold-out': this.soldOut,
        'is-end': !((this.index + 1) % this.totalCols),
        'is-over': this.isOver
      }
    },
    shopifyId() {
      return this.product?.shopify?.product.id
    },
    soldOut() {
      return this.available === false
    },
    backgroundColorResting() {
      return 'transparent'
    },
    backgroundColorHover() {
      const productColorBackgroundLast = last(
        this.product.colors?.backgroundColors
      )
      return productColorBackgroundLast?.hex ?? this.backgroundColor
    },
    textColorHover() {
      return this.product.colors?.textColor?.hex
    }
  },

  methods: {
    over() {
      this.isOver = true
      this.$emit('over', this.index)
    },
    out() {
      this.isOver = false
      this.$emit('out', this.index)
    }
  }
}
</script>

<style>
.product-grid-item__image,
.product-grid-item__title {
  position: relative;
  z-index: 2;
}

.product-grid-item__image {
  flex: 1 1 auto;
  margin-bottom: var(--grid-track-gutter);
  margin-top: var(--grid-track-gutter);
  transition: opacity var(--fade-speed);
}

.is-sold-out .product-grid-item__image {
  opacity: 0.7;
}

.product-grid-item__badge {
  left: 1.25em;
  top: 1.25em;
  z-index: 3;
}

.product-grid-item__title {
  flex: 0 1 auto;
}

.product-grid-item__sold-out {
  display: inline-block;
  height: auto;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 60%;
  z-index: 3;
}

.product-grid-item.link.is-over {
  background-color: var(--grid-item-background-color-hover);
  color: var(--grid-item-text-color-hover);
}

@media (min-width: theme('screens.xs')) {
  .product-grid-item.link.is-over {
    background-color: var(--grid-item-background-color-hover);
    color: var(--grid-item-text-color-hover);
    transform: translate(8px, -8px);
    z-index: 4;
  }

  .product-grid-item.link.is-end.is-over {
    transform: translate(-8px, -8px);
    z-index: 5;
  }
}

@screen xs {
  .product-grid-item.link {
    transition: transform var(--hover-speed);
  }

  [data-js-focus-visible] .product-grid-item.link[data-focus-visible-added] {
    transform: translate(8px, -8px);
    z-index: 4;
  }

  [data-js-focus-visible]
    .product-grid-item.link.is-end[data-focus-visible-added] {
    transform: translate(-8px, -8px);
    z-index: 5;
  }
}
</style>
